export type ChatSourceKind = "document" | "wiki" | "other";

export const chatSourceKinds: ChatSourceKind[] = ["document", "wiki", "other"];

export const chatSourcesCopy = {
  title: "Источники",
  empty: "Появятся после ответа.",
  emptyGroup: "Нет",
  document: "Документы",
  documentOne: "Документ",
  wiki: "Wiki",
  other: "Связи",
  relation: "Связь",
  fileFragment: "Фрагмент файла",
  wikiWording: "Формулировка Wiki",
  openDocument: "Открыть фрагмент",
  openWiki: "Открыть Wiki",
  disagrees: "Не сходится с",
  backToList: "К списку",
  validityActive: "Действует",
  validityExpired: "Устарело",
  validityUpcoming: (from: string) =>
    from ? `Вступит с ${from}` : "Вступит позже",
} as const;

export type ChatSourcesView = "split" | "list" | "quote";

export function chatSourcesClass(view: ChatSourcesView): string {
  if (view === "split") {
    return "chat-sources";
  }
  return `chat-sources chat-sources--swap chat-sources--${view}`;
}

export type ChatSourceRow<T> = {
  key: string;
  items: T[];
};

type ChatSourceItem = {
  entityId?: string | null;
  relation?: boolean;
  textEdited?: boolean;
  validity?: {
    status: "active" | "expired" | "upcoming";
    label: string;
  };
};

export function chatSourceKind(item: ChatSourceItem): ChatSourceKind {
  if (item.relation) {
    return "other";
  }
  return item.entityId ? "wiki" : "document";
}

export function chatSourceKindLabel(kind: ChatSourceKind): string {
  if (kind === "wiki") {
    return chatSourcesCopy.wiki;
  }
  if (kind === "other") {
    return chatSourcesCopy.relation;
  }
  return chatSourcesCopy.documentOne;
}

export function groupChatSources<T extends ChatSourceItem>(
  items: T[],
): Record<ChatSourceKind, T[]> {
  const groups: Record<ChatSourceKind, T[]> = {
    document: [],
    wiki: [],
    other: [],
  };
  for (const item of items) {
    const kind = chatSourceKind(item);
    groups[kind].push(item);
    if (kind !== "document") {
      groups.document.push(item);
    }
  }
  return groups;
}

export function visibleChatSourceKinds(
  groups: Record<ChatSourceKind, readonly unknown[]>,
): ChatSourceKind[] {
  return chatSourceKinds.filter((kind) => groups[kind].length > 0);
}

export function collapseChatSourceRows<
  T extends { documentId: string; index: number },
>(kind: ChatSourceKind, items: T[]): ChatSourceRow<T>[] {
  if (kind !== "document") {
    return items.map((item) => ({
      key: String(item.index),
      items: [item],
    }));
  }

  const rows: ChatSourceRow<T>[] = [];
  const indexByDoc = new Map<string, number>();
  for (const item of items) {
    const at = indexByDoc.get(item.documentId);
    if (at == null) {
      indexByDoc.set(item.documentId, rows.length);
      rows.push({ key: item.documentId, items: [item] });
      continue;
    }
    rows[at]!.items.push(item);
  }
  return rows;
}

export function chatSourceSection(item: {
  heading: string | null;
  sectionPath: string;
  version: number;
}): string {
  return item.heading || item.sectionPath || `версия ${item.version}`;
}

export function chatSourceVersionLabel(version: number): string {
  return `версия ${version}`;
}

export function chatSourceCiteLabel(count: number): string {
  const n = Math.abs(count) % 100;
  const n1 = n % 10;
  if (n > 10 && n < 20) {
    return `${count} цитат`;
  }
  if (n1 === 1) {
    return `${count} цитата`;
  }
  if (n1 >= 2 && n1 <= 4) {
    return `${count} цитаты`;
  }
  return `${count} цитат`;
}

export function chatSourceMeta(
  item: ChatSourceItem & {
    heading: string | null;
    sectionPath: string;
    version: number;
  },
  kind?: ChatSourceKind,
): string {
  const group = kind ?? chatSourceKind(item);
  if (group === "wiki") {
    return chatSourceSection(item);
  }
  if (group === "other") {
    return chatSourcesCopy.relation;
  }
  return chatSourceSection(item);
}

export function chatSourceRowTitle<
  T extends { documentTitle: string; excerpt?: string },
>(item: T, kind: ChatSourceKind): string {
  if (kind === "other") {
    const excerpt = item.excerpt?.trim();
    if (excerpt) {
      return excerpt;
    }
  }
  return item.documentTitle;
}

export function chatSourceRowMeta<
  T extends ChatSourceItem & {
    documentTitle: string;
    heading: string | null;
    sectionPath: string;
    version: number;
  },
>(items: T[], kind: ChatSourceKind): string | null {
  const lead = items[0];
  if (!lead) {
    return null;
  }
  if (kind === "other") {
    return lead.documentTitle;
  }
  if (kind === "wiki") {
    return chatSourceSection(lead);
  }
  if (items.length === 1) {
    return `${chatSourceSection(lead)} · ${chatSourceVersionLabel(lead.version)}`;
  }
  const sections = [...new Set(items.map(chatSourceSection))];
  if (sections.length === 1) {
    return `${sections[0]} · ${chatSourceCiteLabel(items.length)}`;
  }
  return chatSourceCiteLabel(items.length);
}

export function chatSourceExcerpt(
  item: { excerpt: string; documentExcerpt?: string },
  kind: ChatSourceKind,
): string {
  if (kind === "document" && item.documentExcerpt) {
    return item.documentExcerpt;
  }
  return item.excerpt;
}

export type ChatSourceQuoteBlock = {
  label: string;
  text: string;
  edited?: boolean;
};

export function chatSourceQuoteBlocks(
  item: {
    excerpt: string;
    documentExcerpt?: string;
    textEdited?: boolean;
  },
  kind: ChatSourceKind,
): ChatSourceQuoteBlock[] {
  const fileText = item.documentExcerpt?.trim();
  const wikiText = item.excerpt.trim();
  if (item.textEdited && fileText && fileText !== wikiText) {
    if (kind === "document") {
      return [
        { label: chatSourcesCopy.fileFragment, text: fileText },
        { label: chatSourcesCopy.wikiWording, text: wikiText, edited: true },
      ];
    }
    return [
      { label: chatSourcesCopy.wikiWording, text: wikiText, edited: true },
      { label: chatSourcesCopy.fileFragment, text: fileText },
    ];
  }
  return [
    {
      label: "",
      text: chatSourceExcerpt(item, kind),
      ...(kind === "wiki" && item.textEdited ? { edited: true } : {}),
    },
  ];
}

export function chatSourceShowsEdited(
  kind: ChatSourceKind,
  textEdited?: boolean,
): boolean {
  return kind === "wiki" && textEdited === true;
}

export function chatSourceConflictPartners<
  T extends { index: number; contradicts?: number[] },
>(item: T, items: T[]): T[] {
  const byIndex = new Map(items.map((row) => [row.index, row]));
  return (item.contradicts ?? []).flatMap((index) => {
    const other = byIndex.get(index);
    return other && other.index !== item.index ? [other] : [];
  });
}

export function chatSourcePreview(
  item: {
    documentTitle: string;
    entityId?: string | null;
    relation?: boolean;
    heading: string | null;
    sectionPath: string;
    version: number;
  },
  kind?: ChatSourceKind,
): { title: string; kindLabel: string; meta: string } {
  return {
    title: item.documentTitle,
    kindLabel: chatSourceKindLabel(kind ?? chatSourceKind(item)),
    meta: `${chatSourceSection(item)} · ${chatSourceVersionLabel(item.version)}`,
  };
}

export function chatSourceValidityTagType(
  status: "active" | "expired" | "upcoming",
): "green" | "gray" | "blue" {
  if (status === "expired") {
    return "gray";
  }
  /* Active + upcoming: blue outline tags (see .chat-source-validity-tag). */
  return "blue";
}

export function chatCiteClass(
  item: ChatSourceItem,
  selected = false,
  kind?: ChatSourceKind,
): string {
  const resolved = kind ?? chatSourceKind(item);
  const edited = chatSourceShowsEdited(resolved, item.textEdited);
  const expired = item.validity?.status === "expired";
  return `chat-cite chat-cite--${resolved}${edited ? " chat-cite--edited" : ""}${expired ? " chat-cite--expired" : ""}${selected ? " is-on" : ""}`;
}
